// web/src/screens/AddPhoto.tsx (/photo) — "Send us photos" (portal spec 19, G7): photo-only uploads, no change asked.
// Each photo goes through the upload link (PhotoPicker); one kind:'photo' request carries an `Uploaded image:` line per
// photo. Live sites: we use them in the next monthly update. Before go-live (sold → going live): we use them on the new
// site. Open from stage `sold` on (the API 409s a demo or an ended site), so the picker only offers those sites.
import { useCallback, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { apiErrorText } from '../lib/errors';
import { PageHeader } from '../components/PageHeader';
import { FormField } from '../components/FormField';
import { Icon } from '../components/Icon';
import { PhotoPicker } from '../components/PhotoPicker';
import { toast } from '../components/Toast';

const PHOTO_STAGES = ['sold', 'finishing', 'going_live', 'live', 'paused', 'legacy'];
const PRE_LIVE = ['sold', 'finishing', 'going_live'];

export function AddPhoto() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => api.listSites() });
  const [siteId, setSiteId] = useState(params.get('site') ?? '');
  const [photos, setPhotos] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const onPhotos = useCallback((urls: string[], up: boolean) => { setPhotos(urls); setUploading(up); }, []);

  const open = (sites.data ?? []).filter((s) => PHOTO_STAGES.includes(s.stage));
  const site = open.find((s) => s.id === siteId) ?? open[0];
  const preLive = !!site && PRE_LIVE.includes(site.stage);

  async function submit() {
    if (!site || !photos.length) return;
    setBusy(true);
    try {
      const title = photos.length === 1 ? 'Photo for the site' : `${photos.length} photos for the site`;
      const body = [...(note.trim() ? [note.trim(), ''] : []), ...photos.map((u) => `Uploaded image: ${u}`)].join('\n');
      const { id } = await api.submitRequest(site.id, title, body, 'photo');
      qc.invalidateQueries({ queryKey: ['requests'] });
      qc.invalidateQueries({ queryKey: ['home'] });
      toast({ kind: 'success', message: preLive ? "Thanks! We'll use these on your new site." : "Thanks! We'll use these in your next monthly update." });
      nav(`/requests/${id}`);
    } catch (e) {
      toast({ kind: 'error', message: /photo uploads open/.test(apiErrorText(e, '')) ? 'Photos open up once you’ve signed up.' : "Couldn't send those — please try again." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button onClick={() => nav(-1)} className="mb-4 inline-flex min-h-11 items-center gap-1 text-sm text-muted hover:text-ink"><Icon name="arrow-left" size={16} />Back</button>
      <PageHeader title="Send us photos"
        subtitle={preLive ? "We'll use these on your new site." : "We'll use these in your next monthly update."} />
      {sites.isSuccess && !site ? (
        <p className="max-w-xl rounded-lg border border-hair bg-surface p-4 text-sm text-ink">Photos open up once you’ve signed up.</p>
      ) : (
        <div className="flex max-w-xl flex-col gap-5">
          {open.length > 1 && (
            <div className="flex flex-col gap-1.5">
              <label htmlFor="photo-site" className="text-sm font-medium text-ink">Which site are these for?</label>
              <select id="photo-site" value={site?.id ?? ''} onChange={(e) => setSiteId(e.target.value)}
                className="min-h-11 w-full rounded-md border border-hair bg-bg px-3 py-2.5 text-ink">
                {open.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
          {site && <PhotoPicker key={site.id} optional={false} onChange={onPhotos} disabled={busy} />}
          <FormField label="Anything we should know?" name="photo-note" textarea rows={3} value={note} onChange={setNote}
            helper="Optional — e.g. “These are from our new kitchen remodels.”" />
          {!preLive && (
            <p className="text-xs text-muted">Want them on the site sooner? <button onClick={() => nav(`/request?site=${site?.id ?? ''}`)} className="min-h-11 text-info underline">Ask for a change</button> and attach them.</p>
          )}
          <button onClick={submit} disabled={busy || uploading || !site || !photos.length}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-md bg-run px-4 font-medium text-runink transition-transform active:scale-[0.98] disabled:opacity-50">
            <Icon name="image" size={16} />{busy ? 'Sending…' : 'Send photos'}
          </button>
        </div>
      )}
    </>
  );
}
